/**
 * Salesforce Enhanced Chat API Constants and Configuration
 * Based on messaging-web-api-sample-app patterns with config integration
 */

import { getSalesforceConfig, getSalesforceEndpoints } from '../config/salesforceConfig';

// Get configuration from environment
const config = getSalesforceConfig();
const endpoints = getSalesforceEndpoints();

// Salesforce Entry Types for API requests
export const SALESFORCE_ENTRY_TYPES = {
  CONVERSATION_TYPING_STARTED_INDICATOR: 'TypingStartedIndicator',
  CONVERSATION_TYPING_STOPPED_INDICATOR: 'TypingStoppedIndicator'
};

// Salesforce API Configuration
export const SALESFORCE_CONFIG = {
  API_BASE_URL: config.API_BASE_URL,
  ORGANIZATION_ID: config.ORGANIZATION_ID,
  DEVELOPER_NAME: config.DEVELOPER_NAME,
  CAPABILITIES_VERSION: config.CAPABILITIES_VERSION,
  PLATFORM: config.PLATFORM,
  SALESFORCE_VERSION: config.SALESFORCE_VERSION
};

// API Endpoints (integrated with config)
export const SALESFORCE_ENDPOINTS = {
  JWT_URL: endpoints.JWT_URL,
  CONVERSATIONS_URL: endpoints.CONVERSATIONS_URL,
  MESSAGES_URL: endpoints.MESSAGES_URL,
  TYPING_URL: endpoints.TYPING_URL,
  EVENT_SOURCE_URL: endpoints.EVENT_SOURCE_URL
};

// Salesforce API Constants
export const SALESFORCE_CONSTANTS = {
  // Capabilities and platform info
  CAPABILITIES_VERSION: config.CAPABILITIES_VERSION || '1.0',
  PLATFORM: config.PLATFORM || 'web',
  
  // Connection and timeout settings
  HEARTBEAT_TIMEOUT: config.CONNECTION_TIMEOUT || 90000,
  MAX_RECONNECT_ATTEMPTS: config.EVENT_SOURCE_MAX_RETRIES || 5,
  RECONNECT_BASE_DELAY: config.EVENT_SOURCE_RECONNECT_TIMEOUT || 1000,
  
  // API endpoints paths
  API_PATHS: {
    // Salesforce v2 API for authenticated users with Cognito JWT
    AUTHENTICATED_TOKEN: '/iamessage/api/v2/authorization/authenticated/access-token',
    CONTINUATION_TOKEN: '/iamessage/api/v2/authorization/continuation-access-token',
    CONVERSATION: '/iamessage/api/v2/conversation',
    CONVERSATION_EVENTS: '/iamessage/api/v2/conversation/events',
    CONVERSATION_MESSAGE: '/iamessage/api/v2/conversation/{conversationId}/message',
    TYPING_INDICATOR: '/iamessage/api/v2/conversation/{conversationId}/typing'
  },
  
  // Entry types for API requests
  ENTRY_TYPES: SALESFORCE_ENTRY_TYPES
};

// Storage Keys for web storage
export const STORAGE_KEYS = {
  JWT: 'SALESFORCE_JWT',
  ORGANIZATION_ID: 'SALESFORCE_ORGANIZATION_ID',
  DEPLOYMENT_DEVELOPER_NAME: 'SALESFORCE_DEPLOYMENT_DEVELOPER_NAME',
  MESSAGING_URL: 'SALESFORCE_MESSAGING_URL',
  CONVERSATION_ID: 'SALESFORCE_CONVERSATION_ID',
  CONVERSATION_START_TIME: 'SALESFORCE_CONVERSATION_START_TIME',
  LAST_EVENT_ID: 'SALESFORCE_LAST_EVENT_ID'
};

// Salesforce Event Types from Server-Sent Events
export const SALESFORCE_EVENT_TYPES = {
  CONVERSATION_MESSAGE: 'CONVERSATION_MESSAGE',
  CONVERSATION_PARTICIPANT_CHANGED: 'CONVERSATION_PARTICIPANT_CHANGED',
  CONVERSATION_ROUTING_RESULT: 'CONVERSATION_ROUTING_RESULT',
  CONVERSATION_ROUTING_WORK_RESULT: 'CONVERSATION_ROUTING_WORK_RESULT',
  CONVERSATION_TYPING_STARTED_INDICATOR: 'CONVERSATION_TYPING_STARTED_INDICATOR',
  CONVERSATION_TYPING_STOPPED_INDICATOR: 'CONVERSATION_TYPING_STOPPED_INDICATOR',
  CONVERSATION_DELIVERY_ACKNOWLEDGEMENT: 'CONVERSATION_DELIVERY_ACKNOWLEDGEMENT',
  CONVERSATION_READ_ACKNOWLEDGEMENT: 'CONVERSATION_READ_ACKNOWLEDGEMENT',
  CONVERSATION_CLOSE_CONVERSATION: 'CONVERSATION_CLOSE_CONVERSATION'
};

// Conversation Status
export const CONVERSATION_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  CONNECTING: 'CONNECTING',
  WAITING_FOR_AGENT: 'WAITING_FOR_AGENT',  // NEW: Routed but no agent accepted yet (0-30s)
  ACTIVE: 'ACTIVE',                        // Agent has joined and can chat
  AGENT_UNAVAILABLE: 'AGENT_UNAVAILABLE',  // NEW: First timeout occurred, still waiting (30-60s)
  NO_AGENTS_FOUND: 'NO_AGENTS_FOUND',      // NEW: Second timeout, no agents available (60s+)
  ENDED: 'ENDED',
  ERROR: 'ERROR'
};

// Connection Status
export const CONNECTION_STATUS = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  ERROR: 'ERROR'
};

// Session Initialization States
export const SESSION_INIT_STATE = {
  NOT_STARTED: 'not-started',
  INITIALIZING: 'initializing',
  INITIALIZED: 'initialized'
};

// Salesforce Routing Attributes
export const ROUTING_ATTRIBUTES = {
  SOURCE: 'Routing_Source__c',
  USER_ID: 'Routing_User_ID__c',
  LOCALE: 'Routing_Locale__c'
};

// Timer Configuration
export const TIMER_CONFIG = {
  AGENT_WAIT_TIMEOUT: 30, // 30 seconds
  AUTO_TIMER_DELAY: 100   // 100ms delay for loading check
};

// Environment-based Salesforce Configuration
const getEnvironment = () => {
  // Determine environment from various sources
  if (process.env.NODE_ENV === 'development') return 'dev';
  if (process.env.REACT_APP_ENV === 'staging' || process.env.REACT_APP_ENV === 'sta') return 'staging';
  if (process.env.REACT_APP_ENV === 'production' || process.env.REACT_APP_ENV === 'prod') return 'prod';
  return 'dev'; // default
};

// Validate Salesforce configuration
export const validateSalesforceConfig = (configToValidate) => {
  const { ORGANIZATION_ID, DEVELOPER_NAME, API_BASE_URL } = configToValidate;
  
  if (!ORGANIZATION_ID || !DEVELOPER_NAME || !API_BASE_URL) {
    throw new Error('Missing required Salesforce configuration parameters');
  }
  
  // Validate org ID format (15 or 18 character Salesforce ID)
  if (!/^[a-zA-Z0-9]{15}([a-zA-Z0-9]{3})?$/.test(ORGANIZATION_ID)) {
    throw new Error('Invalid Salesforce Organization ID format');
  }
  
  // Validate messaging URL format
  if (!API_BASE_URL.startsWith('https://')) {
    throw new Error('Messaging URL must use HTTPS protocol');
  }
  
  return true;
};